/**
 * src/api/adminAIContext.js - what the Administration AI is told, and when it is
 * asked at all.
 *
 * Two pure, side-effect-free pieces (unit-tested in adminAIContext.test.js):
 *
 *  1. classifyQuery(text)
 *     Decides whether the Ask bar input is a short VIEW COMMAND ("what's ready
 *     to bill?", "failed syncs", "overdue") that the deterministic screens can
 *     answer exactly, or a real question that must go to the AI. It is a strict
 *     whitelist: a query becomes a canned view ONLY if every word is a known
 *     view word or filler. The previous substring matcher sent anything
 *     containing e.g. "late" (calculate, template), "current" (currently) or
 *     "qb" to a canned view and never reached the AI.
 *
 *  2. buildAdminAiContext({ tasks, ... })
 *     Builds the read-only snapshot sent with a question. It is assembled from
 *     the task list and QuickBooks sync log the page has ALREADY loaded - no new
 *     API calls, no new data leaving the TMS beyond what is listed here - using
 *     the SAME predicates as the screens (isReadyToInvoice, isOverdueActive ...)
 *     so the AI's numbers always agree with the tables on the page.
 *
 *     Layout (a language model is poor at arithmetic over long lists, so the
 *     facts it will be asked about are pre-computed):
 *       DEFINITIONS   what "active", "open/unfinished", "overdue" ... mean here
 *       TOTALS        counts over ALL tasks
 *       BY ...        per-status / customer / driver / billing aggregates (ALL tasks)
 *       TASK ROWS     a prioritised SAMPLE: rows matching the question first,
 *                     then overdue, active, exceptions, recent
 *       TRUNCATION    explicit "showing X of N" so the model never mistakes a
 *                     sample for the whole
 *
 *     Deliberately NOT included: street addresses, phone numbers, contact
 *     names, delivery instructions, dispatch notes, clinical case detail
 *     (surgeon / procedure / trays), POD images, internal UUIDs, emails.
 */

import { isTerminalTaskStatus, normalizeTaskStatus } from "../constants/operations";
import {
  isReadyToInvoice,
  hasFailedQBSync,
  isMissingPOD,
  isOverdueActive,
  getBillableAmount,
} from "./adminAI";

// ─── 1. Query routing ─────────────────────────────────────────────────────────

const VIEW_WORDS = {
  billing_ready: ["invoice", "invoices", "invoiced", "uninvoiced", "invoicing", "bill", "billing", "billable", "ready"],
  failed_sync:   ["sync", "syncs", "synced", "failed", "fail", "failure", "failures", "qb", "quickbooks", "error", "errors"],
  active:        ["active", "inflight", "progress", "running", "current", "currently", "now"],
  missing_pod:   ["pod", "pods", "proof", "missing", "without", "no"],
  overdue:       ["overdue", "late", "stuck", "past", "due", "delayed", "deadline"],
};

// Words that may appear in a view command without changing its meaning.
const FILLER_WORDS = new Set([
  "show", "me", "list", "display", "see", "view", "open", "all", "the", "a", "an", "my", "our",
  "what", "whats", "are", "is", "there", "any", "job", "jobs", "task", "tasks", "ticket", "tickets",
  "order", "orders", "item", "items", "delivery", "deliveries", "delivered", "to", "of", "for",
  "with", "in", "on", "that", "need", "needs", "please",
]);

function wordsOf(raw) {
  return String(raw || "")
    .toLowerCase()
    .replace(/[’']/g, "")            // what's -> whats
    .replace(/in[-\s]?flight/g, "inflight")
    .replace(/in[-\s]?progress/g, "progress")
    .replace(/[^a-z0-9]+/g, " ")
    .split(" ")
    .filter(Boolean);
}

/**
 * @returns {string|null} a view key ("billing_ready" | "failed_sync" | "active" |
 *   "missing_pod" | "overdue") for a short, unambiguous view command; otherwise
 *   null, meaning "send this to the AI".
 */
export function classifyQuery(raw) {
  const words = wordsOf(raw);
  if (words.length === 0 || words.length > 8) return null;

  const matched = new Set();
  for (const word of words) {
    let isViewWord = false;
    for (const [view, list] of Object.entries(VIEW_WORDS)) {
      if (list.includes(word)) {
        matched.add(view);
        isViewWord = true;
      }
    }
    if (!isViewWord && !FILLER_WORDS.has(word)) return null;   // an unknown word => real question
  }

  // "no" / "without" / "delivery" only mean "missing proof" next to "pod"/"proof".
  if (matched.has("missing_pod") && !words.some((w) => w === "pod" || w === "pods" || w === "proof")) {
    matched.delete("missing_pod");
  }
  // "ready" only means "ready to invoice" together with billing words.
  if (matched.has("billing_ready") && words.every((w) => w === "ready" || FILLER_WORDS.has(w))) {
    matched.delete("billing_ready");
  }
  // "now" / "current" / "due" alone are too weak to be a command.
  if (matched.has("active") && words.every((w) => ["now", "current", "currently"].includes(w) || FILLER_WORDS.has(w))) {
    matched.delete("active");
  }

  return matched.size === 1 ? [...matched][0] : null;   // none or several => AI
}

// ─── 2. Context building ──────────────────────────────────────────────────────

export const AI_CONTEXT_BUDGET_CHARS = 6000;   // client-side target; backend accepts up to 8000

const STOP_WORDS = new Set([
  "the", "and", "are", "for", "with", "that", "this", "have", "has", "had", "been", "from", "into",
  "how", "many", "much", "which", "who", "whom", "what", "when", "where", "why", "any", "still",
  "show", "list", "tell", "give", "about", "there", "their", "them", "than", "then", "some", "does",
  "did", "can", "could", "would", "should", "all", "our", "your", "you", "job", "jobs", "task",
  "tasks", "ticket", "tickets", "order", "orders", "open", "opened", "unfinished", "status",
  "today", "week", "month", "year", "last", "next", "most", "least", "total", "number", "count",
]);

const HISTORY_INTENT = /\b(deliver|complet|finish|done|cancel|fail|histor|recent|yesterday|last|week|month|ago|billed|invoiced|revenue|paid)/i;

function clean(value, max = 60) {
  return String(value ?? "")
    .replace(/[^\u0020-\u007e\u00a0-\uffff]+/g, " ")  // newlines / control chars
    .replace(/\|/g, "/")                              // our column separator
    .replace(/end\s*context/gi, "end-context")        // cannot close the data block
    .replace(/\brequest\s*:/gi, "request -")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, max);
}

function maskIdentity(value) {
  const v = String(value ?? "").trim();
  if (!v || v.toLowerCase() === "unassigned") return "";
  // Never send an email address: keep only the part before "@".
  return clean(v.includes("@") ? v.split("@")[0] : v, 40);
}

/** createdAt/updatedAt are written by the backend as naive UTC ISO strings. */
function asUtcDate(value) {
  if (!value) return null;
  let v = String(value);
  if (/^\d{4}-\d{2}-\d{2}T[\d:.]+$/.test(v)) v += "Z";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Deadlines follow the same rules as the Overdue view (isOverdueActive). */
function deadlineDate(task) {
  const raw =
    task.deliveryDeadline ||
    task.delivery_deadline ||
    task.milestones?.deliveryDeadline ||
    task.caseDeadline ||
    task.surgeryTime ||
    null;
  if (!raw) return null;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

function customerOf(task) {
  return clean(task.clientName || task.hospitalName || task.companyName || "Unknown", 40) || "Unknown";
}

function makeFormatters(timeZone) {
  const opts = (o) => (timeZone ? { ...o, timeZone } : o);
  const dt = new Intl.DateTimeFormat("en-US", opts({ month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }));
  const day = new Intl.DateTimeFormat("en-CA", opts({ year: "numeric", month: "2-digit", day: "2-digit" }));
  const dayLabel = new Intl.DateTimeFormat("en-US", opts({ weekday: "short", month: "short", day: "numeric" }));
  const full = new Intl.DateTimeFormat("en-US", opts({
    weekday: "long", year: "numeric", month: "long", day: "numeric", hour: "numeric", minute: "2-digit", timeZoneName: "short",
  }));
  const money = new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" });
  return {
    dt: (d) => (d ? dt.format(d) : "-"),
    dayKey: (d) => day.format(d),
    dayLabel: (d) => dayLabel.format(d),
    full: (d) => full.format(d),
    money: (n) => money.format(n),
  };
}

function countBy(items, keyFn, limit) {
  const map = new Map();
  for (const item of items) {
    const key = keyFn(item);
    map.set(key, (map.get(key) || 0) + 1);
  }
  const sorted = [...map.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  const shown = sorted.slice(0, limit).map(([k, n]) => `${k} ${n}`).join("; ");
  const more = sorted.length - limit;
  return (shown || "none") + (more > 0 ? `; (+${more} more)` : "");
}

function questionTokens(question) {
  const out = new Set();
  for (const w of String(question || "").toLowerCase().match(/[a-z0-9][a-z0-9-]{2,}/g) || []) {
    if (!STOP_WORDS.has(w)) out.add(w);
  }
  return [...out];
}

/**
 * @param {object}   args
 * @param {object[]} args.tasks          tasks as returned by GET /tasks/
 * @param {string}   [args.question]     used to pull in the rows it is about
 * @param {Date}     [args.now]
 * @param {string}   [args.timeZone]     IANA zone (default: the browser's)
 * @param {number}   [args.budget]       max characters (default AI_CONTEXT_BUDGET_CHARS)
 * @returns {string}
 */
export function buildAdminAiContext({
  tasks,
  question = "",
  now = new Date(),
  timeZone,
  budget = AI_CONTEXT_BUDGET_CHARS,
} = {}) {
  const f = makeFormatters(timeZone);
  const list = Array.isArray(tasks) ? tasks.filter((t) => t && typeof t === "object") : [];

  // ── per-task facts, computed once with the SAME predicates the screens use ──
  const infos = list.map((task, index) => {
    const key = normalizeTaskStatus(task.status);
    const terminal = isTerminalTaskStatus(task.status);
    const amount = getBillableAmount(task);
    const created = asUtcDate(task.createdAt || task.created_at);
    const updated = asUtcDate(task.updatedAt || task.updated_at || task.podReceivedAt);
    return {
      task,
      uid: String(task.id || task.publicId || `idx${index}`),
      ref: clean(task.publicId || String(task.id || `#${index + 1}`).slice(0, 8), 24),
      status: clean(task.status || "Unknown", 24),
      key,
      terminal,
      active: !terminal,
      paused: key === "paused",
      delivered: key === "delivered" || key === "completed",
      overdue: isOverdueActive(task),
      missingPod: isMissingPOD(task),
      failedSync: hasFailedQBSync(task),
      readyToInvoice: isReadyToInvoice(task),
      amount,
      customer: customerOf(task),
      driver: maskIdentity(task.assignedDriver || task.driver),
      deadline: deadlineDate(task),
      created,
      updated,
    };
  });

  const active = infos.filter((i) => i.active);
  const overdue = infos.filter((i) => i.overdue);
  const missingPod = infos.filter((i) => i.missingPod);
  const failedSync = infos.filter((i) => i.failedSync);
  const ready = infos.filter((i) => i.readyToInvoice);
  const paused = infos.filter((i) => i.paused);
  const delivered = infos.filter((i) => i.delivered);
  const canceled = infos.filter((i) => i.key === "canceled" || i.key === "failed");
  const unassignedActive = active.filter((i) => !i.driver);
  const sum = (arr) => arr.reduce((s, i) => (i.amount != null ? s + i.amount : s), 0);

  // ── header: time, definitions, totals ────────────────────────────────────
  const lines = [];
  lines.push(`SNAPSHOT of the Call It In TMS (read-only). Now: ${f.full(now)}.`);
  lines.push(
    "DEFINITIONS: Active = status is not Delivered/Completed/Canceled/Failed/Paused. " +
      "Open or unfinished = Active + Paused. Overdue = not finished (Active or Paused) and past its delivery deadline. " +
      "Ready to invoice = Delivered/Completed with no QuickBooks invoice. " +
      "Missing POD = Delivered/Completed with no proof on file. Billable $ = task total."
  );
  lines.push(
    `TOTALS (all ${infos.length} tasks): active ${active.length}; paused ${paused.length}; ` +
      `open-or-unfinished ${active.length + paused.length}; delivered/completed ${delivered.length}; ` +
      `canceled/failed ${canceled.length}; unassigned active ${unassignedActive.length}; overdue ${overdue.length}; ` +
      `ready to invoice ${ready.length} (${f.money(sum(ready))}); failed QuickBooks sync ${failedSync.length}; ` +
      `delivered without POD ${missingPod.length}.`
  );
  lines.push(`BY STATUS: ${countBy(infos, (i) => i.status, 14)}`);
  lines.push(`ACTIVE BY CUSTOMER: ${countBy(active, (i) => i.customer, 10)}`);
  lines.push(`ACTIVE BY DRIVER: ${countBy(active, (i) => i.driver || "UNASSIGNED", 10)}`);
  if (overdue.length) lines.push(`OVERDUE BY CUSTOMER: ${countBy(overdue, (i) => i.customer, 8)}`);
  if (ready.length) {
    const perCustomer = new Map();
    for (const i of ready) perCustomer.set(i.customer, (perCustomer.get(i.customer) || 0) + (i.amount || 0));
    const top = [...perCustomer.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([c, v]) => `${c} ${f.money(v)}`).join("; ");
    lines.push(`READY TO INVOICE BY CUSTOMER: ${top}`);
  }
  const billedByCustomer = new Map();
  for (const i of delivered) {
    if (i.amount != null) billedByCustomer.set(i.customer, (billedByCustomer.get(i.customer) || 0) + i.amount);
  }
  if (billedByCustomer.size) {
    const top = [...billedByCustomer.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8)
      .map(([c, v]) => `${c} ${f.money(v)}`).join("; ");
    lines.push(`DELIVERED VALUE BY CUSTOMER: ${top}`);
  }

  // Tasks created per day for the last 7 days (in the viewer's time zone).
  const dayCounts = new Map();
  for (const i of infos) if (i.created) dayCounts.set(f.dayKey(i.created), (dayCounts.get(f.dayKey(i.created)) || 0) + 1);
  const recentDays = [];
  for (let back = 0; back < 7; back += 1) {
    const d = new Date(now.getTime() - back * 86_400_000);
    recentDays.push(`${f.dayLabel(d)} ${dayCounts.get(f.dayKey(d)) || 0}`);
  }
  lines.push(`TASKS CREATED, LAST 7 DAYS (newest first): ${recentDays.join("; ")}`);

  // ── rows: a prioritised sample, matched to the question ───────────────────
  const tokens = questionTokens(question);

  // What the question is about, besides names: which exception flags it mentions.
  const q = String(question || "");
  const intentFlags = [
    [/overdue|\blate\b|stuck|past due|delayed|behind schedule/i, "overdue"],
    [/\bpod\b|proof/i, "missingPod"],
    [/invoice|bill|unbilled|revenue|owed|receivable/i, "readyToInvoice"],
    [/sync|quickbooks|\bqb\b/i, "failedSync"],
  ].filter(([re]) => re.test(q)).map(([, flag]) => flag);
  const flagHits = (i) => intentFlags.reduce((s, flag) => s + (i[flag] ? 1 : 0), 0);

  // Rows that mention a name / order number / facility from the question,
  // ranked by name matches first, then by how many of the question's flags they carry.
  const matched = tokens.length
    ? infos
        .map((i) => {
          const hay = [i.ref, i.customer, i.driver, i.status,
            clean(i.task.pickupFacilityName, 60), clean(i.task.dropoffFacilityName || i.task.hospitalName, 60)]
            .join(" ").toLowerCase();
          return { i, score: tokens.reduce((s, t) => s + (hay.includes(t) ? 1 : 0), 0) };
        })
        .filter((x) => x.score > 0)
        .sort((a, b) => b.score * 2 + flagHits(b.i) - (a.score * 2 + flagHits(a.i)))
        .map((x) => x.i)
    : [];
  // Rows carrying a flag the question asks about (e.g. "failed syncs"), even with no name match.
  const flagged = intentFlags.length ? infos.filter((i) => flagHits(i) > 0) : [];

  const byDeadline = (a, b) => (a.deadline?.getTime() ?? Infinity) - (b.deadline?.getTime() ?? Infinity);
  const newestFirst = (a, b) => (b.created?.getTime() ?? 0) - (a.created?.getTime() ?? 0);
  const recentTerminal = infos.filter((i) => i.terminal && !i.paused).sort(newestFirst);

  const order = [];
  const seen = new Set();
  const take = (arr, cap) => {
    let n = 0;
    for (const i of arr) {
      if (n >= cap) break;
      if (!seen.has(i.uid)) { seen.add(i.uid); order.push(i); n += 1; }
    }
  };
  take(matched, 20);
  take([...flagged].sort((a, b) => flagHits(b) - flagHits(a) || byDeadline(a, b)), 20);
  take([...overdue].sort(byDeadline), 15);
  take([...active].sort(byDeadline), 30);
  take([...paused].sort(newestFirst), 8);
  take([...missingPod].sort(newestFirst), 8);
  take([...failedSync].sort(newestFirst), 8);
  take([...ready].sort(newestFirst), 8);
  take(recentTerminal, HISTORY_INTENT.test(question) ? 25 : 10);

  const rowText = (i) => {
    const t = i.task;
    const route = `${clean(t.pickupFacilityName, 28) || "?"} > ${clean(t.dropoffFacilityName || t.hospitalName, 28) || "?"}`;
    const flags = [
      i.overdue && "OVERDUE", i.missingPod && "NO-POD", i.failedSync && "QB-FAILED",
      i.readyToInvoice && "UNINVOICED", t.isEmergency && "EMERGENCY",
    ].filter(Boolean).join(",");
    return [
      i.ref, i.status, i.customer, route, i.driver || "UNASSIGNED",
      `due ${f.dt(i.deadline)}`, i.amount != null ? f.money(i.amount) : "-",
      `created ${f.dt(i.created)}`, flags,
    ].filter((p) => p !== "").join(" | ");
  };

  const header = lines.join("\n");
  const rowsTitle = "TASK ROWS (ref | status | customer | pickup > dropoff | driver | deadline | $ | created | flags), " +
    "priority order: matches for the question, overdue, active, paused, exceptions, recent:";
  const reserve = 220;   // truncation notice + separators
  let remaining = budget - header.length - rowsTitle.length - reserve;
  const rows = [];
  for (const i of order) {
    const text = rowText(i);
    if (text.length + 1 > remaining) break;
    rows.push(text);
    remaining -= text.length + 1;
  }

  const omitted = infos.length - rows.length;
  const notice =
    omitted > 0
      ? `ROWS SHOWN: ${rows.length} of ${infos.length} tasks. ${omitted} tasks are not listed above, but every count ` +
        "in TOTALS and BY sections covers ALL tasks. If an answer needs a task that is not listed, say the rows are partial."
      : `ROWS SHOWN: all ${infos.length} tasks.`;

  const out = [header, rowsTitle, ...(rows.length ? rows : ["(no tasks loaded)"]), notice].join("\n");
  return out.length <= budget ? out : out.slice(0, budget);
}
